import React, { useState, useRef } from 'react';

import Translate, { useI18n } from '@hydra/common/components/i18n';
import Icon from '@hydra/common/components/Icon';
import MenuItem from '@hydra/common/components/MenuItem';
import settings from '@hydra/utils/settings';
import { sendEvent } from '@hydra/utils/gtm';
import fetch from '@hydra/utils/fetch';
import logger from '@hydra/utils/logger';

import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  content: { marginLeft: theme.spacing(1) },
  pre: {
    display: 'block',
    fontSize: theme.typography.fontSizeMedium,
    lineHeight: 1.6,
    color: theme.palette.grey[900],
    margin: 0,
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
    userSelect: 'text',
    cursor: 'text'
  },
  paper: {
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  btnMobile: {
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  }
}));

const getFacebookUrl = ({ freeReviewUrl, title, summary }) => {
  const woorankLogo = new URL('assets/img/woorank-logo-glyph.png', settings.get('cdnUrl')).href;
  const images = encodeURIComponent(woorankLogo);
  const url = encodeURIComponent(freeReviewUrl);
  return `http://www.facebook.com/sharer/sharer.php?s=100&p[images][0]=${images}&p[url]=${url}&p[title]=${title}&p[summary]=${summary}`;
};

const getTwitterUrl = ({ freeReviewUrl, translate }) => {
  const url = translate('social_sharing_tweet', { with: { reviewShortLink: freeReviewUrl } });
  return `http://twitter.com/compose/tweet?url=${url}`;
};

const getLinkedinUrl = ({ freeReviewUrl, title, summary }) => {
  const url = encodeURIComponent(freeReviewUrl);
  return `http://www.linkedin.com/shareArticle?mini=true&source=WooRank&url=${url}&title=${title}&summary=${summary}`;
};

const ShareReview = ({ url }) => {
  const classes = useStyles();
  const { translate, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [shortUrl, setShortUrl] = useState(null);
  const shareBtn = useRef(null);
  const freeReviewUrl = `https://www.woorank.com/${locale}/www/${url}`;
  const title = translate('social_sharing_title');
  const summary = translate('social_sharing_text');

  const shareUrls = [
    {
      id: 'facebook',
      icon: 'facebook',
      link: getFacebookUrl({ freeReviewUrl, title, summary })
    },
    {
      id: 'twitter',
      icon: 'twitter',
      link: getTwitterUrl({ freeReviewUrl, translate })
    },
    {
      id: 'linkedin',
      icon: 'linkedin',
      link: getLinkedinUrl({ freeReviewUrl, title, summary })
    }
  ];

  const openMenu = () => {
    sendEvent('freereview-actions-share-btn');
    setOpen(true);
  };
  const closeMenu = () => setOpen(false);
  const showShortUrl = async () => {
    if (shortUrl) {
      return;
    }
    try {
      const response = await fetch(`/api/shortUrl?url=${url}&lang=${locale}`);
      const json = await response.json();
      setShortUrl(json);
    } catch (err) {
      logger.error({
        event: 'generate-short-url-failed',
        err
      });
      setShortUrl(translate('error_oops'));
    }
  };

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        className={classes.btnMobile}
        ref={(el) => {
          shareBtn.current = el;
        }}
        onClick={openMenu}
      >
        <Translate content='sharing_review_share_title' />
      </Button>
      <Menu
        id='share-dropdown'
        anchorEl={shareBtn.current}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        getContentAnchorEl={null}
        onClose={closeMenu}
        classes={{
          paper: classes.paper
        }}
      >
        <MenuItem
          onClick={showShortUrl}
          divider
        >
          {shortUrl
            ? (
              <Tooltip placement='top' title={translate('copy_url')}>
                <pre
                  className={classes.pre}
                  onClick={e => e.stopPropagation()}
                >
                  {shortUrl}
                </pre>
              </Tooltip>
              )
            : (
              <>
                <Icon type='links' size='md' />
                <Translate content='short_url' className={classes.content} />
              </>
              )}
        </MenuItem>
        {shareUrls.map(({ id, icon, link }, key) => (
          <MenuItem
            divider={(key + 1) !== shareUrls.length}
            component='a'
            href={link}
            target='_blank'
            key={key}
          >
            <Icon type={icon} size='md' />
            <Translate content={`share_${id}`} className={classes.content} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ShareReview;
